<script setup lang="ts">
import type { IconConstant } from '@tpg/grammar-web-icons';
import { tpgIconZoomInMap } from '@tpg/grammar-web-icons';
import { computed } from 'vue';

import type {
  GrammarMenuItemProps,
} from '@nora-monorepo/grammar-ui';
import type { SymbolDTO } from '@nora-monorepo/nora-api-client';
import { useWebViewStore } from '@nora-monorepo/ui-shared';

import MenuItemRow from '#nora-ui/map/components/overlay-menu/primitives/MenuItemRow.vue';
import { useMapStore } from '#nora-ui/map/stores/map-store';

/**
 * A single track row (user or tracker). Both share the same visibility toggle and
 * "Go to" behaviour, so callers normalise their DTO into these primitive fields.
 */
interface TrackMenuItemProps {
  trackId: string;
  title: string;
  icon: IconConstant;
  goToLabel: string;
  mapId: string;
  customSymbol?: SymbolDTO | null;
}

const props = defineProps<TrackMenuItemProps>();

const { trackId, mapId } = props;

const webViewStore = useWebViewStore();
const mapStore = useMapStore();

const map = mapStore.getMapByIdOrThrow(mapId);

const visible = computed(() => {
  return map.state.track.isTrackVisible(trackId);
});

function onVisibilityChange(newVisibility: boolean) {
  const visibilityState = map.state.track.booleanToVisibilityState(newVisibility);

  map.state.track.setTrackVisibility(trackId, visibilityState);
}

const trackPosition = computed(() => {
  const trackPosition = map.state.track.getLastKnownTrackPositionForProducerId(trackId);

  if (!trackPosition) {
    return;
  }

  return trackPosition;
});

function onGoToTrack() {
  if (!trackPosition.value?.latitude || !trackPosition.value?.longitude) {
    return;
  }

  map.setCenter({
    latitude: trackPosition.value.latitude,
    longitude: trackPosition.value.longitude,
    zoom: 15,
  });

  if (webViewStore.isWebView) {
    map.state.draw.setDrawLayerMenuOpen(false);
  }
}

const menuItems = computed(() => {
  const items: GrammarMenuItemProps[] = [];
  if (trackPosition.value) {
    items.push({
      label: props.goToLabel,
      onSelect: onGoToTrack,
      leadingIconTemplate: tpgIconZoomInMap,
    });
  }

  return items;
});
</script>

<template>
  <MenuItemRow
    :title="title"
    :menu-items="menuItems"
    :visible="visible"
    :icon="icon"
    :custom-symbol="customSymbol"
    @visibility-changed="onVisibilityChange"
  />
</template>
